import { type FrameData, cancelFrame, frame } from 'motion/react'
import { useCallback, useRef, useState, useSyncExternalStore } from 'react'

/**
 * Timeline helpers for the looping product scenes on the home page.
 *
 * A scene is a list of keyframes: at `at` milliseconds into the loop the scene
 * enters `phase`. `phaseAt` is pure and unit-tested; `useSceneClock` drives it
 * from the browser's animation frame without a `useEffect`, resets when the
 * tile leaves the viewport, and jumps to the final phase under reduced motion.
 *
 * Every frame loop here goes through `useFrameLoop`, which is subscribed to
 * motion's frame loop only while it is enabled. An off-screen tile, a
 * finished count-up and reduced motion schedule no frames at all, so a page
 * left idle never wakes the main thread.
 */
export interface SceneKeyframe<P extends string = string> {
	/** Milliseconds from the start of the loop. The first keyframe is at 0. */
	at: number
	phase: P
}

const REDUCED_QUERY = '(prefers-reduced-motion: reduce)'
function subscribeReducedMotion(onChange: () => void) {
	if (typeof window === 'undefined') return () => {}
	const query = window.matchMedia(REDUCED_QUERY)
	query.addEventListener('change', onChange)
	return () => query.removeEventListener('change', onChange)
}
function readReducedMotion() {
	return window.matchMedia(REDUCED_QUERY).matches
}
function readReducedMotionOnServer() {
	return false
}

/**
 * `prefers-reduced-motion`, hydration-safe. The server and the hydrating
 * render both see `false`, so markup never differs between them; React then
 * re-renders once with the real value. Use this instead of motion's
 * `useReducedMotion` anywhere the value changes what is rendered.
 */
export function useReducedMotionSafe() {
	return useSyncExternalStore(
		subscribeReducedMotion,
		readReducedMotion,
		readReducedMotionOnServer,
	)
}

/**
 * Which phase a scene is in `elapsedMs` after it started. With `loopMs` the
 * timeline repeats; without it the last phase is held forever.
 */
export function phaseAt<P extends string>(
	elapsedMs: number,
	keyframes: readonly SceneKeyframe<P>[],
	loopMs?: number,
): P {
	const first = keyframes[0]
	if (!first) {
		throw new Error('phaseAt needs at least one keyframe')
	}
	const t =
		loopMs && loopMs > 0
			? ((elapsedMs % loopMs) + loopMs) % loopMs
			: Math.max(0, elapsedMs)
	let current = first.phase
	for (const frame of keyframes) {
		if (frame.at <= t) current = frame.phase
		else break
	}
	return current
}

/** Eased progress 0..1 for a span of the timeline (cubic ease-out). */
export function easeOutProgress(elapsedMs: number, from: number, to: number) {
	if (to <= from) return elapsedMs >= to ? 1 : 0
	const k = Math.min(1, Math.max(0, (elapsedMs - from) / (to - from)))
	return 1 - (1 - k) ** 3
}

export interface SceneClockOptions<P extends string> {
	/** True while the tile is in view. Leaving resets the loop. */
	active: boolean
	keyframes: readonly SceneKeyframe<P>[]
	/** Repeat after this many ms. Omit to play once and hold. */
	loopMs?: number
	/** What to show with reduced motion: usually the end state. */
	finalPhase: P
}

/**
 * Whether a clock still needs animation frames. A clock runs only while its
 * tile is in view and motion is allowed, and stops for good once it has
 * nothing left to change: a count-up that reached its target, or a timeline
 * without a loop that reached its last keyframe.
 */
export function clockShouldRun({
	active,
	reduced,
	finished,
}: {
	active: boolean
	reduced: boolean
	finished: boolean
}) {
	return active && !reduced && !finished
}

/** When a timeline that does not loop stops changing (its last keyframe). */
export function lastKeyframeAt(keyframes: readonly SceneKeyframe[]) {
	return keyframes.reduce((last, frame) => Math.max(last, frame.at), 0)
}

const NO_SNAPSHOT = () => 0
const NO_UNSUBSCRIBE = () => {}

/**
 * Calls `onFrame(elapsedMs)` on every animation frame while `enabled`, with
 * `elapsedMs` counted from the first frame after it became enabled. While
 * disabled nothing is subscribed, so motion's frame loop can go idle.
 * Disabling rewinds: the next enabled run starts again from 0.
 *
 * The subscription is a `useSyncExternalStore` subscribe function (React
 * subscribes it after commit and unsubscribes it when it changes or the
 * component unmounts), which is the house alternative to an effect. A new
 * `onFrame` each render only re-subscribes; the start time is kept.
 */
export function useFrameLoop(
	enabled: boolean,
	onFrame: (elapsedMs: number) => void,
) {
	const startRef = useRef<number | null>(null)
	const subscribe = useCallback(() => {
		if (!enabled) {
			startRef.current = null
			return NO_UNSUBSCRIBE
		}
		const tick = ({ timestamp }: FrameData) => {
			if (startRef.current === null) startRef.current = timestamp
			onFrame(timestamp - startRef.current)
		}
		frame.update(tick, true)
		return () => cancelFrame(tick)
	}, [enabled, onFrame])
	useSyncExternalStore(subscribe, NO_SNAPSHOT, NO_SNAPSHOT)
}

/**
 * Current phase of a scene, re-rendering only when the phase changes. The
 * clock starts the first frame after `active` becomes true and resets when
 * it becomes false, so a scene replays each time it scrolls into view, as
 * the reference does. A timeline without `loopMs` stops taking frames once
 * it reaches its last keyframe.
 */
export function useSceneClock<P extends string>({
	active,
	keyframes,
	loopMs,
	finalPhase,
}: SceneClockOptions<P>): { phase: P; elapsedMs: number } {
	const reduced = useReducedMotionSafe()
	const initial = (keyframes[0] ?? { phase: finalPhase }).phase
	const [phase, setPhase] = useState<P>(initial)
	const [finished, setFinished] = useState(false)
	const [wasActive, setWasActive] = useState(active)
	const elapsedRef = useRef(0)

	// Adjusting state during render (the React-documented pattern) instead of
	// an effect: when the tile scrolls out, rewind so it replays on return.
	if (active !== wasActive) {
		setWasActive(active)
		if (!active) {
			elapsedRef.current = 0
			if (phase !== initial) setPhase(initial)
			if (finished) setFinished(false)
		}
	}

	useFrameLoop(clockShouldRun({ active, reduced, finished }), (elapsed) => {
		elapsedRef.current = elapsed
		const next = phaseAt(elapsed, keyframes, loopMs)
		if (next !== phase) setPhase(next)
		if (!loopMs && elapsed >= lastKeyframeAt(keyframes)) setFinished(true)
	})

	if (reduced) return { phase: finalPhase, elapsedMs: Number.MAX_SAFE_INTEGER }
	return { phase, elapsedMs: elapsedRef.current }
}

/**
 * A number that counts from 0 to `target` over `durationMs` with a cubic
 * ease-out, starting when `active` turns true. Re-renders on every frame
 * while counting, so keep the rendered subtree small (a clock, a bar). It
 * stops taking frames once it reaches the target.
 */
export function useCountUp({
	active,
	target,
	durationMs,
}: {
	active: boolean
	target: number
	durationMs: number
}): { value: number; done: boolean } {
	const reduced = useReducedMotionSafe()
	const [value, setValue] = useState(0)
	const [wasActive, setWasActive] = useState(active)
	if (active !== wasActive) {
		setWasActive(active)
		if (!active && value !== 0) setValue(0)
	}
	const finished = value >= target
	useFrameLoop(clockShouldRun({ active, reduced, finished }), (elapsed) => {
		const next = target * easeOutProgress(elapsed, 0, durationMs)
		if (next !== value) setValue(next)
	})
	if (reduced) return { value: target, done: true }
	return { value, done: finished }
}

/** m:ss for the timeline clocks. */
export function formatClock(seconds: number) {
	const whole = Math.max(0, Math.floor(seconds))
	const m = Math.floor(whole / 60)
	const s = whole % 60
	return `${m}:${s < 10 ? '0' : ''}${s}`
}
